import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, ClipboardPaste, History, MapPin, X } from 'lucide-react';
import type { ClienteRecurrenteVista, ProductoVista } from '@shared/types';
import type { PedidoExtraido } from '@shared/parsePedidoWa';
import { PegarPedidoModal } from './PegarPedidoModal';
import { Modal } from '@renderer/components/ui/Modal';
import { etiquetaPaso } from './pasosDomicilio';
import { SeccionDondeEntregar, type EntregaInfo } from './DondeEntregarForm';
import { formatPrecio } from '@renderer/utils/formatters';

/**
 * Sprint 7.2: recogía sólo la identidad del cliente (nombre + celular).
 * Sprint 35: lookup automático de cliente recurrente por teléfono y "pegar el mensaje".
 *
 * ## Sprint 98 — "¿Quién y a dónde?"
 *
 * Reporte del dueño del producto, revisando el flujo entero: *"aún lo siento con muchos pasos"*.
 * Eran cuatro —quién / a dónde / el menú / el envío— y al contarlos con el oficio delante, sólo
 * **uno** sobraba: los otros no son pasos del sistema sino **esperas de terceros** (la cotización
 * del grupo de motomandados y la transferencia del cliente), y ésos no se quitan con código.
 *
 * El que sobraba era éste: quien contesta el teléfono pregunta *"¿quién habla y a dónde es?"* en
 * la misma respiración. Son una sola pregunta, y estaban en dos pantallas.
 *
 * Se conserva lo que el Sprint 66 había ganado —la dirección se pide **antes** del menú, para
 * poder decir *"hasta allá no llegamos"* antes de capturar la comida—, sólo que ya no cuesta una
 * pantalla extra. Y para un cliente conocido esta ventana no tiene **nada** que teclear: el
 * teléfono trae su nombre, su dirección y su punto en el mapa.
 */
export interface ClienteIdentidad {
  nombre: string;
  telefono?: string;
  /**
   * Sprint 66 — El cliente aceptó recibir promociones por WhatsApp.
   *
   * `undefined` = no se preguntó en esta captura, y eso NO es un "no": si el cliente ya había
   * aceptado, tomarle un pedido no puede darlo de baja.
   */
  aceptaPublicidad?: boolean;
}

/** Sprint 35 — Datos auxiliares que se pasan al siguiente paso del flow. */
export interface DatosClienteAvanzado {
  recurrente?: ClienteRecurrenteVista | null;
  pedidoExtraido?: PedidoExtraido | null;
}

interface Props {
  onSiguiente: (
    cliente: ClienteIdentidad,
    entrega: EntregaInfo,
    extras?: DatosClienteAvanzado,
  ) => void;
  onAtras: () => void;
  inicial?: ClienteIdentidad | null;
  /** Lo que ya se sabía de la entrega: del cliente recurrente, del chat, o de haber vuelto atrás. */
  entregaInicial?: EntregaInfo | null;
  /**
   * Sprint 98 — El catálogo, para que aquí mismo se pueda leer el pedido del chat.
   *
   * Este formulario dejó de ser sólo "quién es": es **la puerta** del pedido por WhatsApp. Pegar el
   * mensaje una vez tiene que dar las dos cosas —quién escribe y qué quiere—, así que el intérprete
   * del menú vive ahora aquí y necesita el menú.
   */
  productos: ProductoVista[];
  monedaSimbolo: string;
  /** Lo que se entendió del chat entra al carrito por el camino normal del POS. */
  onLineasDelChat: (lineas: Array<{ productoId: number; nombre: string; cantidad: number }>) => void;
}

export function QuienYADondeForm({
  onSiguiente,
  onAtras,
  inicial,
  entregaInicial,
  productos,
  monedaSimbolo,
  onLineasDelChat,
}: Props) {
  const [nombre, setNombre] = useState(inicial?.nombre ?? '');
  const [telefono, setTelefono] = useState(inicial?.telefono ?? '');
  const [error, setError] = useState<string | null>(null);
  const [entrega, setEntrega] = useState<EntregaInfo>(
    entregaInicial ?? { direccion: '', referenciasEntrega: '' },
  );
  /** Lo que se leyó del chat en esta captura: sirve para confirmarlo aquí, no sólo para el carrito. */
  const [leidoDelChat, setLeidoDelChat] = useState<
    Array<{ nombre: string; cantidad: number; precio: number }>
  >([]);

  /**
   * Sprint 66 — Consentimiento de publicidad. Arranca apagado: el default de un permiso es no
   * tenerlo.
   */
  const [aceptaPublicidad, setAceptaPublicidad] = useState(false);

  // Sprint 35 — estado de lookup cliente recurrente.
  const [recurrente, setRecurrente] = useState<ClienteRecurrenteVista | null>(null);
  const [recurrenteIgnorado, setRecurrenteIgnorado] = useState(false);
  const [buscando, setBuscando] = useState(false);

  // Sprint 35 — pedido extraído del modal "Pegar".
  const [modalPegarAbierto, setModalPegarAbierto] = useState(false);
  const [pedidoExtraido, setPedidoExtraido] = useState<PedidoExtraido | null>(null);

  /**
   * Sprint 98 — Si el sistema ya sabe quién es, lo escribe él.
   *
   * Antes, encontrar al cliente por su teléfono terminaba en un botón *"Usar estos datos"*: el
   * sistema lo tenía, lo mostraba en pantalla, y aun así le pedía al operador un clic para
   * copiarlo al campo de arriba. Pedir un dato que ya se sabe es de las cosas que más rápido
   * enseñan a desconfiar de un sistema.
   *
   * Se llena solo **sólo si el campo está vacío** —lo que el operador escribe no se pisa nunca— y
   * la tarjeta cambia de "usa estos datos" a "¿no es él?", que es la única duda que queda.
   */
  const [nombreAutollenado, setNombreAutollenado] = useState(false);
  const nombreRef = useRef(nombre);
  nombreRef.current = nombre;

  // Autocomplete por teléfono con debounce.
  useEffect(() => {
    const telDigits = telefono.replace(/\D/g, '');
    if (telDigits.length < 7 || recurrenteIgnorado) {
      setRecurrente(null);
      return;
    }
    setBuscando(true);
    const t = setTimeout(async () => {
      const res = await window.api.clientes.porTelefono(telefono);
      setBuscando(false);
      if (res.ok && res.data) {
        const encontrado = res.data;
        setRecurrente(encontrado);
        // Se lee de la ref y no del estado para no re-disparar la búsqueda en cada tecla del nombre.
        if (encontrado.nombre && nombreRef.current.trim().length === 0) {
          setNombre(encontrado.nombre);
          setNombreAutollenado(true);
        }
        /*
          Sprint 98 — Ahora la dirección vive en esta misma ventana, así que el cliente conocido
          la trae de una vez. Antes la promesa era "se llenará en el siguiente paso"; ya no hay
          siguiente paso que esperar. Sólo si está vacía: lo que el operador escribió, manda.
        */
        if (encontrado.direccion) {
          setEntrega((prev) =>
            prev.direccion.trim().length > 0
              ? prev
              : {
                  direccion: encontrado.direccion ?? '',
                  latitud: encontrado.latitud ?? undefined,
                  longitud: encontrado.longitud ?? undefined,
                  referenciasEntrega: encontrado.referencias ?? prev.referenciasEntrega,
                },
          );
        }
      } else {
        setRecurrente(null);
      }
    }, 400);
    return () => {
      clearTimeout(t);
      setBuscando(false);
    };
  }, [telefono, recurrenteIgnorado]);

  const usarRecurrente = () => {
    if (!recurrente) return;
    if (recurrente.nombre) setNombre(recurrente.nombre);
    // No tocamos el teléfono (ya está: es por donde lo encontramos).
    if (recurrente.direccion) {
      setEntrega({
        direccion: recurrente.direccion,
        latitud: recurrente.latitud ?? undefined,
        longitud: recurrente.longitud ?? undefined,
        referenciasEntrega: recurrente.referencias ?? undefined,
      });
    }
  };

  /**
   * Sprint 98 — "Es otra persona": se descarta el hallazgo Y lo que escribió por su cuenta.
   *
   * Si el nombre se llenó solo y resulta que es otro cliente, dejarlo en el campo sería peor que
   * no haberlo llenado: el operador sigue de largo y el pedido sale a nombre de quien no es.
   */
  const olvidarRecurrente = () => {
    setRecurrenteIgnorado(true);
    if (nombreAutollenado) {
      setNombre('');
      setNombreAutollenado(false);
    }
  };

  /**
   * Sprint 98 — Lo que salió de pegar el chat, una sola vez: los productos y quién los pidió.
   *
   * Los productos se los lleva el POS al carrito por su camino normal (con sus topes de stock y su
   * precio de catálogo). Lo del cliente se **propone** en los campos: sólo llena lo que está vacío,
   * porque lo que el operador ya escribió vale más que lo que adivinó un parser sobre el texto de
   * un tercero.
   *
   * Sprint 98 — La dirección y el punto del mapa también entran aquí. El mensaje del cliente
   * normalmente trae las tres cosas —qué quiere, quién es y a dónde— y ahora las tres caben en
   * esta ventana: un solo pegado puede dejar el pedido listo para confirmar.
   */
  const aplicarLoLeido = (
    lineas: Array<{ productoId: number; nombre: string; cantidad: number }>,
    extraido?: PedidoExtraido | null,
  ) => {
    if (lineas.length > 0) {
      onLineasDelChat(lineas);
      /*
        Sprint 98 — Se guarda lo que salió del chat para poder MOSTRARLO aquí.

        Observación del dueño del producto viendo el camino corto funcionar de punta a punta: si el
        sistema ya sacó quién, qué y a dónde de un solo mensaje, esta ventana **ya no captura:
        confirma**, y el título tiene que decir eso. Pero si promete "qué", el pedido tiene que
        verse — y estaba sólo en el carrito, detrás de la ventana.
      */
      setLeidoDelChat(
        lineas.map((l) => ({
          nombre: l.nombre,
          cantidad: l.cantidad,
          precio: productos.find((p) => p.id === l.productoId)?.precio ?? 0,
        })),
      );
    }
    if (!extraido) return;
    setPedidoExtraido(extraido);
    if (extraido.nombre && !nombre) setNombre(extraido.nombre);
    if (extraido.telefono && !telefono) setTelefono(extraido.telefono);
    if (extraido.direccion || extraido.latitud) {
      setEntrega((prev) =>
        prev.direccion.trim().length > 0
          ? prev
          : {
              direccion: extraido.direccion ?? prev.direccion,
              latitud: extraido.latitud ?? prev.latitud,
              longitud: extraido.longitud ?? prev.longitud,
              referenciasEntrega: extraido.referencias ?? prev.referenciasEntrega,
            },
      );
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (nombre.trim().length < 2) {
      setError('Ingresa al menos el nombre del cliente');
      return;
    }
    if (telefono && !/^[\d\s+()-]{6,20}$/.test(telefono.trim())) {
      setError('Teléfono no válido');
      return;
    }
    /*
      Sprint 98 — La dirección se exige aquí, que es donde el Sprint 66 quería que se exigiera:
      antes del menú. Lo que cambió es que ya no cuesta una pantalla propia.
    */
    if (entrega.direccion.trim().length < 3) {
      setError('Escribe a dónde hay que llevarlo, o marca el punto en el mapa');
      return;
    }
    onSiguiente(
      {
        nombre: nombre.trim(),
        telefono: telefono.trim() || undefined,
        // Sólo viaja si se preguntó de verdad (ver `preguntarPublicidad`).
        aceptaPublicidad: preguntarPublicidad ? aceptaPublicidad : undefined,
      },
      {
        direccion: entrega.direccion.trim(),
        latitud: entrega.latitud,
        longitud: entrega.longitud,
        referenciasEntrega: entrega.referenciasEntrega?.trim() || undefined,
      },
      {
        recurrente: recurrente && !recurrenteIgnorado ? recurrente : null,
        pedidoExtraido,
      },
    );
  };

  /**
   * ¿Toca preguntar por la publicidad?
   *
   * Sólo con celular —sin número no hay a dónde escribir— y sólo si este cliente no contestó
   * antes. A un recurrente que ya dijo sí no se le vuelve a preguntar, y a uno que dijo no
   * tampoco: repreguntar en cada pedido es cómo se le enseña al operador a marcar cualquier
   * cosa para poder avanzar.
   */
  const preguntarPublicidad =
    telefono.trim().length >= 10 && recurrente?.aceptaPublicidad === undefined;

  /**
   * ¿Esto es una confirmación o una captura?
   *
   * Confirmación cuando el chat trajo el pedido **y** las dos cosas que hacen falta para llegar:
   * quién es y a dónde va. Si falta cualquiera de las tres, todavía hay trabajo que hacer y la
   * ventana se comporta —y se llama— como lo que es: una captura.
   */
  const esConfirmacion =
    leidoDelChat.length > 0 && nombre.trim().length >= 2 && entrega.direccion.trim().length >= 3;

  const totalLeido = leidoDelChat.reduce((s, l) => s + l.precio * l.cantidad, 0);

  const formatFechaRelativa = (iso: string) => {
    try {
      const d = new Date(iso);
      const diff = Date.now() - d.getTime();
      const horas = Math.floor(diff / (1000 * 60 * 60));
      // Sprint 98 — Un pedido de hace veinte minutos decía "hace 0 h". Cero horas no es un dato:
      // es el redondeo asomándose a la pantalla.
      if (horas < 1) return 'hace un rato';
      if (horas < 24) return `hace ${horas} h`;
      const dias = Math.floor(horas / 24);
      if (dias < 30) return `hace ${dias} d`;
      const meses = Math.floor(dias / 30);
      return `hace ${meses} mes${meses === 1 ? '' : 'es'}`;
    } catch {
      return iso.slice(0, 10);
    }
  };

  return (
    <>
      <Modal
        abierto
        /*
          Sprint 98 — El título dice lo que de verdad toca hacer.

          Hay dos historias, y hasta ahora las dos se llamaban igual:

          - **El cliente ordenado** (o el que usó la plantilla): el mensaje traía nombre, celular,
            dirección y la lista, el sistema lo sacó todo y aquí no hay nada que teclear — sólo
            revisar. Llamarle "¿Quién y a dónde?" hacía que el operador se pusiera a buscar qué
            escribir en una ventana que ya estaba llena.
          - **El camino largo**: llegó "mándame 2 pollos" y hay que preguntar lo demás. Ahí sí es
            una captura, y el título pregunta.

          Lo decide el estado real de los campos, no una bandera: si el sistema lo llenó y sigue
          lleno, esto es una confirmación.
        */
        titulo={esConfirmacion ? 'Confirma quién, qué y a dónde' : '¿Quién y a dónde?'}
        subtitulo={
          esConfirmacion
            ? `${etiquetaPaso('cliente')} · Lo leí de su mensaje. Revisa que esté bien y sigue.`
            : `${etiquetaPaso('cliente')} · Con esto se puede llamar de vuelta y llegar.`
        }
        /* Más ancho que una ficha de dos campos: aquí caben la identidad y la dirección. */
        ancho="lg"
        onSubmit={onSubmit}
        onCerrar={onAtras}
        etiquetaCerrar="Salir sin tomar el pedido"
        pie={
          <div className="flex justify-between">
            <button type="button" onClick={onAtras} className="btn-ghost">
              <ArrowLeft className="h-4 w-4" />
              Atrás
            </button>
            <button type="submit" className="btn-primary">
              Continuar al menú
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        }
      >
        {/*
          Sprint 98 — La única puerta de "me escribió por WhatsApp".

          Antes había dos: la tarjeta del tablero abría un modal que armaba el pedido y **aquí**
          aparecía otro botón que sólo listaba los productos. El operador pegaba, y lo primero que
          veía después era otra invitación a pegar. Ahora se pega una vez y de aquí sale todo: el
          pedido al carrito y sus datos a estos campos.

          Se queda grande y verde: es el trabajo real del negocio, 50 a 150 veces al día.
        */}
        <button
          type="button"
          onClick={() => setModalPegarAbierto(true)}
          data-uso="venta:pegar-chat-cliente"
          className="w-full rounded-xl border-2 border-dashed border-green-300 bg-green-50 hover:bg-green-100 px-4 py-3 flex items-center justify-center gap-2 text-green-800 font-medium transition"
        >
          <ClipboardPaste className="h-4 w-4" />
          {leidoDelChat.length > 0 ? 'Pegar otro mensaje' : 'Pegar lo que me escribió'}
        </button>

        {/*
          Sprint 98 — El "qué", a la vista.

          Si el título dice "confirma quién, QUÉ y a dónde", el pedido no puede estar sólo en el
          carrito de atrás, tapado por esta ventana. Va compacto —una línea por producto y el
          total—: aquí se confirma que se entendió bien, y corregirlo se hace en el menú, que es el
          paso siguiente y tiene sitio para hacerlo.
        */}
        {leidoDelChat.length > 0 && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
            <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-emerald-700">
              <Check className="h-4 w-4" />
              Su pedido, leído del mensaje
            </div>
            <ul className="space-y-0.5 text-sm text-slate-800">
              {leidoDelChat.map((l, k) => (
                <li key={k} className="flex justify-between gap-3">
                  <span className="min-w-0 truncate">
                    <b className="text-primary">{l.cantidad}</b> × {l.nombre}
                  </span>
                  <span className="shrink-0 font-mono">
                    {formatPrecio(l.precio * l.cantidad, monedaSimbolo)}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-1 flex justify-between border-t border-emerald-200 pt-1 text-sm font-semibold">
              <span>Comida</span>
              <span className="font-mono">{formatPrecio(totalLeido, monedaSimbolo)}</span>
            </div>
            <p className="mt-1 text-xs text-emerald-700">
              El envío se cotiza después. En el menú puedes agregar o quitar lo que haga falta.
            </p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="label" htmlFor="cliNom">Nombre *</label>
            <input
              id="cliNom"
              className="input"
              value={nombre}
              onChange={(e) => {
                setNombre(e.target.value);
                // Si lo corrige a mano deja de ser "lo que puso el sistema".
                setNombreAutollenado(false);
              }}
              autoFocus
              placeholder="Juan Pérez"
            />
          </div>
          <div>
            <label className="label" htmlFor="cliTel">Celular</label>
            <input
              id="cliTel"
              className="input"
              value={telefono}
              onChange={(e) => {
                setTelefono(e.target.value);
                setRecurrenteIgnorado(false);
              }}
              placeholder="9931 123456"
              inputMode="tel"
            />
            <p className="mt-1 text-xs text-slate-500">
              {buscando ? 'Buscando si ya es cliente…' : 'Sirve para confirmar el pedido si se corta la llamada.'}
            </p>
          </div>

          {/*
            Sprint 66 — La casilla de publicidad.

            Aparece sólo si hay celular y sólo si este cliente no ha contestado antes. Su texto
            dice qué se le va a mandar y por dónde: "acepta promociones" sin decir el canal es
            una casilla que nadie sabe qué autoriza.
          */}
          {preguntarPublicidad && (
            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <input
                type="checkbox"
                className="mt-0.5 h-5 w-5"
                checked={aceptaPublicidad}
                onChange={(e) => setAceptaPublicidad(e.target.checked)}
              />
              <span className="text-sm">
                <span className="font-medium">Acepta recibir promociones por WhatsApp</span>
                <span className="mt-0.5 block text-xs text-slate-500">
                  Pregúntaselo. Si no lo autoriza, déjalo sin marcar: mandar mensajes sin permiso
                  hace que reporten el número del negocio.
                </span>
              </span>
            </label>
          )}

          {/* Sprint 35 — Tarjeta de cliente recurrente */}
          {recurrente && !recurrenteIgnorado && (
            <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm">
                  <History className="h-4 w-4" />
                  Cliente recurrente — {recurrente.cantidadPedidos} pedido
                  {recurrente.cantidadPedidos === 1 ? '' : 's'} previo
                  {recurrente.cantidadPedidos === 1 ? '' : 's'}
                </div>
                <button
                  type="button"
                  onClick={olvidarRecurrente}
                  aria-label="Cliente nuevo, ignorar"
                  className="p-1 hover:bg-emerald-100 rounded"
                >
                  <X className="h-4 w-4 text-emerald-700" />
                </button>
              </div>
              <div className="text-sm text-slate-700">
                <div>
                  <span className="font-medium">{recurrente.nombre || '(sin nombre)'}</span> ·{' '}
                  <span className="text-emerald-700">{formatFechaRelativa(recurrente.ultimaFecha)}</span>
                </div>
                {recurrente.direccion && (
                  <div className="text-slate-600 mt-0.5">📍 {recurrente.direccion}</div>
                )}
              </div>
              {/*
                Sprint 98 — Dos situaciones distintas, dos botones distintos.

                Si el nombre se llenó solo, ya no hay nada que "usar": lo único que puede estar mal
                es que sea otra persona con el mismo teléfono (una casa, un negocio). Si en cambio
                el operador ya había escrito un nombre diferente, no se le pisa — se le ofrece.
              */}
              {nombreAutollenado ? (
                <button
                  type="button"
                  onClick={olvidarRecurrente}
                  className="w-full btn-secondary text-sm"
                >
                  Es otra persona
                </button>
              ) : (
                <button
                  type="button"
                  onClick={usarRecurrente}
                  className="w-full btn-primary text-sm"
                >
                  Usar estos datos
                </button>
              )}
              <p className="text-xs text-emerald-700">
                {/* Sprint 98 — Ya no hay "siguiente paso" que esperar: la dirección está abajo. */}
                Su dirección y su punto en el mapa ya quedaron puestos aquí abajo.
              </p>
            </div>
          )}
        </div>

        {/*
          Sprint 98 — La segunda mitad de la misma pregunta.

          Separada por una línea y con su propio encabezado, para que se lea como lo que es —"y a
          dónde"— y no como un formulario más largo. El orden importa: primero quién (que trae el
          historial y puede llenar todo lo demás), después a dónde.
        */}
        <div className="mt-5 border-t border-slate-200 pt-4">
          <div className="mb-3 flex items-center gap-2">
            <MapPin className="h-4 w-4 text-slate-500" />
            <h3 className="text-base font-bold">¿A dónde se lo llevamos?</h3>
          </div>
          <SeccionDondeEntregar valor={entrega} onCambio={setEntrega} />
        </div>

        {/*
          Sprint 65/66 — Decir la regla ANTES, no al final.

          Domicilio va con pago por adelantado desde el Sprint 7.2 (el negocio no cocina antes de
          que le paguen). Es una decisión correcta, pero el flujo nunca la mencionaba: el operador
          llegaba al final y descubría que necesitaba un comprobante que el cliente todavía no
          había mandado, con la comida ya capturada.
        */}
        <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <span className="font-semibold">A domicilio se paga antes de cocinar.</span> Al final se
          sube el comprobante de la transferencia, y hasta entonces la orden entra a la cocina.
        </div>

        {error && (
          <div className="error-text mt-3" role="alert">
            {error}
          </div>
        )}
      </Modal>

      <PegarPedidoModal
        abierto={modalPegarAbierto}
        productos={productos}
        monedaSimbolo={monedaSimbolo}
        leerDatosDelCliente
        onCerrar={() => setModalPegarAbierto(false)}
        onAgregar={aplicarLoLeido}
      />
    </>
  );
}

/**
 * @deprecated Sprint 7.2 — alias retro-compatible. Usar `ClienteIdentidad`.
 * Se mantiene para no romper imports en otros componentes durante la migración.
 */
export type ClienteDelivery = ClienteIdentidad;
